import { BulmaLink } from "@c/link";
import { useDoubleClick } from "@gazebo/react/useDoubleClick";
import { NotebookStore, useNotebookStore } from "@store";
import { INoteLocationJSON, IRecursiveFolderJSON, RecursiveThingsInFolder } from "@store/FolderStore";
import { INoteJSON } from "@store/types";
import { sortBy } from "lodash";
import { makeAutoObservable } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDrag, useDrop } from 'react-dnd';
import { v4 as uuid } from 'uuid';

interface IHierarchyItem {
    _id: string;
    name: string;
    children: IHierarchyItem[];
    type: 'folder' | 'note'
}

export const ItemTypes = {
    NOTE: 'note',
    FOLDER: 'folder'
}

const folderNavigationContext = React.createContext<FolderNavigationStore | null>(null)

const FolderNavigationProvider = folderNavigationContext.Provider

const useFolderNavigation = () => {
    const x = useContext(folderNavigationContext)

    if (!x) {
        throw new Error('missing folder navigation context')
    }

    return x
}

class FolderNavigationStore {
    private readonly notebook: NotebookStore;
    public folders: IHierarchyItem[]

    constructor(notebook: NotebookStore) {
        this.notebook = notebook
        this.folders = []

        makeAutoObservable(this, {})
    }

    public addFolder(): void {
        this.folders.push({
            _id: uuid(), name: 'New Folder', children: [], type: 'folder'
        })
    }

    public get list(): IHierarchyItem[] {
        const r: IHierarchyItem[] = [...this.folders]

        this.notebook.notes.docs.forEach(x => {
            r.push({ _id: x._id, name: x.data.title, children: [], type: 'note' })
        })

        return sortBy(r, 'name', '_id')
    }

    public get hierarchy(): IHierarchyItem[] {
        const r: IHierarchyItem[] = this.list

        const filtered = r.filter(x => !this.parent(x))

        return sortBy(filtered, 'name', '_id')
    }

    public parent(x: IHierarchyItem): IHierarchyItem | undefined {
        return this.list.find(l => !!l.children.find(y => x._id === y._id))
    }

    move(item: IHierarchyItem, to: IHierarchyItem) {
        if (to.type === 'note') {
            throw new Error("not implemented.");
        }
        if (item._id === to._id) {
            throw new Error("not implemented.");
        }

        if (item.type === 'folder') {
            const parent = this.parent(item)

            if (parent && parent._id === to._id) {
                // nothing to do.
                return;
            }

            // TODO: Bla, lots of mutations
            this.folders = this.folders.map(l => {
                if (parent && l._id === parent._id) {
                    return { ...l, children: l.children.filter(i => i._id !== item._id) }
                }
                if (l._id === to._id) {
                    return { ...l, children: [...l.children, item] }
                }
                return l
            })
        }

        if (item.type === 'note') {
            const path: string[] = []
            let current: IHierarchyItem | undefined = to

            while (!!current) {
                path.push(current.name)
                current = this.parent(current)
            }

            this.notebook.note(item._id).doc.update((x: INoteJSON) => {
                return {
                    ...x,
                    path
                }
            })

        }
    }
}


const FolderNavigationItemFolder: React.FC<{ item: IRecursiveFolderJSON }> = observer(({ item }) => {
    const store = useNotebookStore()
    const folder = useFolderNavigation()

    const [expand, setExpand] = useState(false)
    const [edit, setEdit] = useState(false)

    const [name, setName] = useState(item.name)

    const onChange = useCallback((e: React.ChangeEvent<any>) => {
        setName(e.target.value)
    }, [setName])

    const onBlur = useCallback((e: React.ChangeEvent<any>) => {
        store.folders.rename(item._id, name)
        setEdit(false)
    }, [name, setEdit])

    const onKeyPress = useCallback((e: React.KeyboardEvent<any>) => {
        if (e.key === 'Enter') {
            store.folders.rename(item._id, name)
            setEdit(false)
        }
    }, [name, setEdit])

    const onSimpleClick: React.MouseEventHandler<any> = useCallback((e) => {
        setExpand(x => !x)
    }, [setExpand])

    const onDoubleClick: React.MouseEventHandler<any> = useCallback((e) => {
        setEdit(true)
    }, [setEdit])

    const onClick = useDoubleClick(onSimpleClick, onDoubleClick)

    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.FOLDER,
        item: item,
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging()
        })
    }))

    // TODO: set typescript
    const [{ isOver, canDrop }, drop] = useDrop(
        () => ({
            accept: [ItemTypes.NOTE, ItemTypes.FOLDER],
            canDrop: (x: RecursiveThingsInFolder, monitor) => {
                if (item._id === x._id) {
                    return false
                }
                console.log('ítem:', item, 'monitor:', monitor)
                return true
            },
            drop: (x: RecursiveThingsInFolder, monitor) => {
                store.folders.move(x._id, item._id)
            },
            hover: (x: RecursiveThingsInFolder, monitor) => {
                if (x.type === 'note') {

                }
                else if (x.type === 'folder') {

                } else {
                    throw new Error('Not handled')
                }

            },
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop()
            })
        }),
        [folder]
    )

    useEffect(() => {
        if (!isOver) {
            return
        }

        const x = setTimeout(() => {
            setExpand(true)
        }, 500)

        return () => clearTimeout(x)
    }, [isOver, setExpand])

    return <>
        <span ref={drop}>
            {
                edit
                    ? <input type="text" value={name} onChange={onChange} onBlur={onBlur} onKeyPress={onKeyPress} />
                    :
                    <a className="is-primary" ref={drag} onClick={onClick}>
                        {
                            expand
                                ? <i className="fal fa-folder-open"></i>
                                : <i className="fal fa-folder"></i>
                        }
                        {" "}{item.name}
                    </a>
            }
        </span>
        {expand &&
            <ul>
                {item.children.map(x => {
                    return <li key={x._id} >
                        <FolderNavigationItem item={x} />
                    </li>
                })}
            </ul>
        }
    </>
})

const FolderNavigationItemNote: React.FC<{ item: INoteLocationJSON }> = observer(({ item }) => {
    const store = useNotebookStore()

    const [{ isDragging }, drag] = useDrag(() => ({
        item: item,
        type: ItemTypes.NOTE,
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging()
        })
    }))

    return <BulmaLink href={store.resolve(item.name)!}>
        <a className="is-primary" ref={drag}>
            <i className="fal fa-file"></i>
            {" "}
            {/* {isDragging ? '(D)' : ''} */}
            {item.name}
        </a>
    </BulmaLink>
})

const FolderNavigationItem: React.FC<{ item: RecursiveThingsInFolder }> = observer(({ item }) => {
    if (item.type === 'note') {
        // @ts-ignore: fix me with a union type
        return <FolderNavigationItemNote item={item} />
    }

    if (item.type === 'folder') {
        // @ts-ignore: fix me with a union type
        return <FolderNavigationItemFolder item={item} />
    }

    throw new Error('missing')
})

export const FolderNavigation: React.FC<{}> = observer(({ }) => {
    const store = useNotebookStore()
    const folderNavigationStore = useMemo(() => new FolderNavigationStore(store), [])

    return (
        <div className="column is-3" style={{ maxHeight: '90vh', overflowY: 'scroll' }}>
            <FolderNavigationProvider value={folderNavigationStore}>
                <h2>Folders</h2>
                <div>
                    <button onClick={() => store.folders.create()}>Create Folder</button>
                </div>
                <ul className="folder-navigation">
                    {store.folders.hierarchy.map(x => (
                        <li key={x._id}>
                            <FolderNavigationItem key={x._id} item={x} />
                        </li>
                    ))}
                </ul>
            </FolderNavigationProvider>
        </div>
    )
})